import { useEffect, useState } from "react";
import { Star, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

export default function ReviewSection({ teacherId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/reviews/${teacherId}`);
    setData(data);
  };

  useEffect(() => { load(); }, [teacherId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Lütfen puan ver");
    if (!comment.trim()) return toast.error("Lütfen yorum yaz");
    setSubmitting(true);
    try {
      await api.post("/reviews", { teacher_id: teacherId, rating, comment });
      toast.success("Yorumun kaydedildi");
      setRating(0); setComment("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-12" data-testid="review-section">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-2xl font-semibold tracking-tight">Değerlendirmeler</h3>
        <div className="text-sm text-slate-500">{data.count} yorum</div>
      </div>

      <div className="mt-4 flex items-center gap-4">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} size={20} fill={n <= Math.round(data.average) ? "var(--sun)" : "none"} stroke="var(--sun)" strokeWidth={1.5} />
          ))}
        </div>
        <div className="font-display text-3xl font-semibold" data-testid="review-average">{data.average.toFixed(1)}</div>
        <div className="text-sm text-slate-500">/ 5.0</div>
      </div>

      {user?.role === "family" && (
        <form onSubmit={submit} className="mt-8 border border-slate-200 rounded-2xl p-6 bg-white">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Yorum bırak</div>
          <div className="mt-3 flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                data-testid={`star-input-${n}`}
                className="active:scale-90 transition-transform"
              >
                <Star size={26} fill={n <= (hover || rating) ? "var(--sun)" : "none"} stroke="var(--sun)" strokeWidth={1.5} />
              </button>
            ))}
          </div>
          <Textarea
            required
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Öğretmenle deneyimini paylaş..."
            className="mt-4 rounded-lg border-slate-300 focus:border-[color:var(--brand)]"
            data-testid="review-comment-input"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="review-submit-btn"
            className="mt-4 inline-flex items-center gap-2 bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white rounded-full px-5 h-10 text-sm font-medium active:scale-95 transition-transform"
          >
            <Send size={14} /> Yorumu Gönder
          </button>
        </form>
      )}

      <div className="mt-8 space-y-4 max-h-[500px] overflow-y-auto pr-2" data-testid="review-list">
        {data.reviews.length === 0 && (
          <p className="text-sm text-slate-500">Henüz yorum yok. İlk yorumu sen bırakabilirsin.</p>
        )}
        {data.reviews.map((r) => (
          <div key={r.id} className="border border-slate-200 rounded-xl p-5 bg-white">
            <div className="flex items-center justify-between">
              <div className="font-medium text-slate-900">{r.family_name}</div>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={14} fill={n <= r.rating ? "var(--sun)" : "none"} stroke="var(--sun)" strokeWidth={1.5} />
                ))}
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{r.comment}</p>
            <div className="mt-2 text-xs text-slate-400">
              {new Date(r.created_at).toLocaleDateString("tr-TR")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
